import { Box, useTheme } from '@mui/material';
import { ISeriesApi } from 'lightweight-charts';
import { debounce, throttle } from 'lodash';
import React, { useCallback, useContext, useRef } from 'react';
import { useState, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';

import './styles.scss';
import VolSeriesChip from '../../../../components/series-chip/vol_series_chip';

import Utils from '../../../../utils/immersive_utils';
import { useChartSetup } from '../../chart/hooks/use-chart-setup';

import NoSeriesPlaceholder from './no-series-placeholder';

import { TooltipContent } from './tooltip-content';
import moment from 'moment';
import {
  Series,
  TimeseriesContext,
  RequestTypeEnum,
} from '../../../../providers/timeseries-provider';

interface SeriesRef {
  id: string;
  label: string;
  color: string;
  ref: ISeriesApi<any>;
}

function findClosestValue(data: any[], targetTime: number) {
  if (!data || data.length === 0) return undefined;
  let closest = data[0];
  let minDiff = Math.abs((closest.time || closest.timestamp) - targetTime);
  for (let i = 1; i < data.length; i++) {
    const t = data[i].time || data[i].timestamp;
    const diff = Math.abs(t - targetTime);
    if (diff < minDiff) {
      closest = data[i];
      minDiff = diff;
    }
  }

  return closest;
}

const getDecimalPlacesForPerpetual = (series: Series[]) => {
  const perpSeries = series.filter((s) => s.type === RequestTypeEnum.PERPETUAL);
  const hasXRP = perpSeries.some((s) => s.currency === 'XRP_USDC' || s.currency === 'XRP');
  const hasSOL = perpSeries.some((s) => s.currency === 'SOL_USDC' || s.currency === 'SOL');

  let decimalPlaces = 2;
  if (hasXRP) {
    decimalPlaces = 4;
  } else if (hasSOL) {
    decimalPlaces = 3;
  }

  return decimalPlaces;
};

const getPriceFormats = (
  hasPerpSeries: boolean,
  hasRRFLYseries: boolean,
  hasVolsSeries: boolean,
  series: Series[],
) => {
  let leftPriceFormat = undefined;
  let rightPriceFormat = undefined;

  if (hasPerpSeries && hasVolsSeries) {
    leftPriceFormat = createPriceFormatter(RequestTypeEnum.PERPETUAL, series);
    rightPriceFormat = createPriceFormatter(RequestTypeEnum.VOLS, series);
  } else if (hasPerpSeries && !hasVolsSeries) {
    rightPriceFormat = createPriceFormatter(RequestTypeEnum.PERPETUAL, series);
  } else if (hasRRFLYseries && !hasPerpSeries) {
    leftPriceFormat = createPriceFormatter(RequestTypeEnum.VOLS, series);
    rightPriceFormat = createPriceFormatter(RequestTypeEnum.VOLS, series);
  } else {
    rightPriceFormat = createPriceFormatter(RequestTypeEnum.VOLS, series);
  }

  return { leftPriceFormat, rightPriceFormat };
};

const createPriceFormatter = (type: RequestTypeEnum, series: Series[]) => {
  if (type === RequestTypeEnum.PERPETUAL) {
    const decimalPlaces = getDecimalPlacesForPerpetual(series);

    return {
      type: 'custom' as const,
      formatter: (price: number) => {
        return price.toFixed(decimalPlaces);
      },
    };
  } else {
    return {
      type: 'custom' as const,
      formatter: (price: number) => {
        return `${(price * 100).toFixed(2)} %`;
      },
    };
  }
};

const getSeriePriceScale = (
  serie: Series,
  hasPerpSeries: boolean,
  hasVolsSeries: boolean,
  hasNoRRFlySeries: boolean,
): 'left' | 'right' => {
  const isRRFLYserie =
    (serie.type === RequestTypeEnum.VOLS && serie.delta.includes('RR')) ||
    serie.delta.includes('FLY');

  if (serie.type === RequestTypeEnum.VOLS && !isRRFLYserie) {
    return 'right';
  }

  if (isRRFLYserie) {
    if (hasPerpSeries) {
      return 'right';
    }

    if (hasNoRRFlySeries) {
      return 'left';
    }
  }

  if (serie.type === RequestTypeEnum.PERPETUAL) {
    return hasVolsSeries ? 'left' : 'right';
  }

  return 'right';
};

const TimeSeriesChart = () => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const timeSeriesWrapperRef = useRef<HTMLDivElement>(null);
  const chipContainerRef = useRef<HTMLDivElement>(null);
  const isChartLoading = false;

  const chart = useRef<any>();
  const seriesRef = useRef<SeriesRef[]>([]);
  const tooltipRef = useRef<HTMLDivElement>();

  const [isVisible, setIsVisible] = useState(true);
  const theme = useTheme();

  const { series, isLoading, updateInitTimeStamp, deleteSeries, fetchingList, isRangeChanged } =
    useContext(TimeseriesContext);
  const seriesLatestRef = useRef<Series[]>(series);

  useEffect(() => {
    seriesLatestRef.current = series;
  }, [series]);

  const isFirstLoadRef = useRef(true);
  const isFirstScrollRef = useRef(false);
  const hasScrolledBackRef = useRef(false);
  const hasTriggeredUpdateRef = useRef(false);

  const [chartHeight, setChartHeight] = useState(340);

  const chipStyle = (isLoading: boolean) => ({
    opacity: isLoading ? 0 : 1,
    pointerEvents: isLoading ? 'none' : ('auto' as React.CSSProperties['pointerEvents']),
  });

  const { initMainChart } = useChartSetup();

  const handleClick = () => {};

  const handleMovement = useCallback((param: any) => {
    const tt = tooltipRef.current!;
    if (!tt) return;

    if (typeof param.time !== 'number' || !param.point || param.point.x < 0 || param.point.y < 0) {
      tt.style.display = 'none';
      return;
    }

    const dateStr = moment
      .unix(param.time as number)
      .utc()
      .locale('en')
      .format("DD MMM 'YY HH:mm:ss");

    const items: Array<{ color: string; name: string; value: number; seriesType?: string }> = [];

    seriesRef.current.forEach((entry) => {
      const serie = seriesLatestRef.current.find((s) => s.id === entry.id);

      if (!serie || !serie.data || serie.data.length === 0) return;

      const closest = findClosestValue(serie.data, param.time);

      if (!closest || closest.value === undefined) return;

      items.push({
        color: entry.color,
        name: entry.label,
        value: closest.value,
        seriesType: serie.type,
      });
    });

    if (items.length === 0) {
      tt.style.display = 'none';
      return;
    }

    const html = ReactDOMServer.renderToStaticMarkup(
      <TooltipContent dateStr={dateStr} items={items} />,
    );
    tt.innerHTML = html;
    tt.style.display = 'block';
    tt.style.left = `${param.point.x + 12}px`;
    tt.style.top = `${param.point.y + 12}px`;
  }, []);

  useEffect(() => {
    if (!chart.current) {
      const actions = { subscribeClick: handleClick, subscribeCrosshairMove: handleMovement };

      initMainChart({
        containerRef: chartContainerRef,
        chartRef: chart,
        actions,
      });

      if (series.length > 0 && chartContainerRef.current && !tooltipRef.current) {
        const tt = document.createElement('div');
        tt.className = 'tv-tooltip-wrapper';
        tt.style.display = 'none';
        chartContainerRef.current.appendChild(tt);
        tooltipRef.current = tt;
      }
    }
  }, [theme, initMainChart, handleMovement]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setIsVisible(entry.isIntersecting);
      });
    });

    if (timeSeriesWrapperRef.current) {
      observer.observe(timeSeriesWrapperRef.current);
    }

    return () => {
      if (timeSeriesWrapperRef.current) {
        observer.unobserve(timeSeriesWrapperRef.current);
      }
    };
  }, []);

  const handleResize = useCallback((height: number) => {
    if (height > 150) {
      setChartHeight(height - 20);
    }
  }, []);

  useEffect(() => {
    if (!isVisible || !timeSeriesWrapperRef.current) {
      return;
    }

    const ro = new ResizeObserver((entries) => {
      const { contentRect } = entries[0];

      if (!contentRect) return;

      handleResize(contentRect.height - getChipDivHeight());
    });

    ro.observe(timeSeriesWrapperRef.current);

    return () => ro.disconnect();
  }, [isVisible]);

  const getChipDivHeight = () => {
    if (chipContainerRef.current) {
      return chipContainerRef.current.clientHeight;
    }
    return 0;
  };

  const handleSeries = useCallback(() => {
    if (chart.current) {
      let newSeries = false;

      const hasPerpSeries = series.some((s) => s.type === RequestTypeEnum.PERPETUAL);
      const hasRRFLYSeries = series.some((s) => s.delta.includes('RR') || s.delta.includes('FLY'));
      const hasVolsSeries = series.some((s) => s.type === RequestTypeEnum.VOLS);
      const hasNoRRFlySeries = series.some(
        (s) =>
          s.type === RequestTypeEnum.VOLS && !(s.delta.includes('RR') || s.delta.includes('FLY')),
      );

      const showLeftScale = hasPerpSeries || hasRRFLYSeries;
      const showRightScale = true;

      const { leftPriceFormat, rightPriceFormat } = getPriceFormats(
        hasPerpSeries,
        hasRRFLYSeries,
        hasVolsSeries,
        series,
      );

      chart.current.applyOptions({
        rightPriceScale: {
          visible: showRightScale,
          ...(rightPriceFormat && { priceFormat: rightPriceFormat }),
        },
        leftPriceScale: {
          visible: showLeftScale,
          ...(leftPriceFormat && { priceFormat: leftPriceFormat }),
        },
      });

      series.forEach((serie) => {
        const alreadyExists = seriesRef.current.find((s) => s.id === serie.id);

        const priceScaleId = getSeriePriceScale(
          serie,
          hasPerpSeries,
          hasVolsSeries,
          hasNoRRFlySeries,
        );

        const serieData = serie.data;

        if (!alreadyExists && serieData && serieData.length > 0) {
          newSeries = true;

          const lineSeriesOptions: any = {
            color: serie.color,
            priceLineVisible: false,
            priceScaleId,
            lineType: 2,
            lineWidth: 1,
            priceFormat: priceScaleId === 'right' ? rightPriceFormat : leftPriceFormat,
          };

          const lineSeries = chart.current.addLineSeries(lineSeriesOptions);

          const nameFormatted = serie.label;

          seriesRef.current.push({
            id: serie.id,
            label: nameFormatted,
            color: serie.color,
            ref: lineSeries,
          });

          lineSeries.setData(serieData);
        } else if (alreadyExists && serieData && serieData.length > 0) {
          const expectedPriceScaleId = getSeriePriceScale(
            serie,
            hasPerpSeries,
            hasVolsSeries,
            hasNoRRFlySeries,
          );

          if (alreadyExists.ref.options().priceScaleId !== expectedPriceScaleId) {
            chart.current.removeSeries(alreadyExists.ref);
            seriesRef.current = seriesRef.current.filter((s) => s.id !== serie.id);

            const lineSeriesOptions: any = {
              color: serie.color,
              priceLineVisible: false,
              priceScaleId: expectedPriceScaleId,
              lineType: 2,
              lineWidth: 1,
              priceFormat: expectedPriceScaleId === 'right' ? rightPriceFormat : leftPriceFormat,
            };

            const newLineSeries = chart.current.addLineSeries(lineSeriesOptions);
            newLineSeries.setData(serieData);

            seriesRef.current.push({
              id: serie.id,
              label: serie.label,
              color: serie.color,
              ref: newLineSeries,
            });

            newSeries = true;
          } else {
            alreadyExists?.ref.setData(serieData);
          }
        }
      });

      if (newSeries || isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        chart.current.timeScale().fitContent();
      }

      if (series.length >= 2 && hasScrolledBackRef.current && !hasTriggeredUpdateRef.current) {
        updateInitTimeStamp();
        hasTriggeredUpdateRef.current = true;
      }

      for (const serieRef of seriesRef.current) {
        const serie = series.find((s) => s.id === serieRef.id);
        if (serie) {
          const priceScaleId = getSeriePriceScale(
            serie,
            hasPerpSeries,
            hasVolsSeries,
            hasNoRRFlySeries,
          );

          const priceFormat = priceScaleId === 'right' ? rightPriceFormat : leftPriceFormat;

          if (priceFormat) {
            serieRef.ref.applyOptions({
              priceFormat: priceFormat,
            });
          }
        }
      }

      chart.current.timeScale().fitContent();
    }
  }, [chart.current, series, updateInitTimeStamp]);

  useEffect(() => {
    if (series.length > 0 && !isLoading) {
      handleSeries();

      if (isRangeChanged.current) {
        isRangeChanged.current = false;
        chart.current?.timeScale().fitContent();
      }

      if (series.length > 0 && chartContainerRef.current && !tooltipRef.current) {
        const tt = document.createElement('div');
        tt.className = 'tv-tooltip-wrapper';
        tt.style.display = 'none';
        chartContainerRef.current.appendChild(tt);
        tooltipRef.current = tt;
      }
    } else {
      if (chart.current && !isLoading) {
        chart.current = null;

        const actions = {
          subscribeClick: handleClick,
          subscribeCrosshairMove: handleMovement,
        };

        initMainChart({
          containerRef: chartContainerRef,
          chartRef: chart,
          actions,
        });

        if (chart.current) chart.current.timeScale().fitContent();
      }
    }
  }, [series, isLoading, handleMovement, isRangeChanged, fetchingList]);

  const handleTimeChange = useCallback(
    throttle(async (param: any) => {
      if (!param || param.from === null || param.to == null) {
        return;
      }

      try {
        const logicalRange = chart.current.timeScale().getVisibleLogicalRange();

        if (logicalRange.from < 0) {
          updateInitTimeStamp();
          isFirstScrollRef.current = true;
          hasScrolledBackRef.current = true;

          return;
        }
      } catch (error) {
        console.error(error);
        return;
      }
    }, 100),
    [updateInitTimeStamp],
  );

  const handleDeleteSerie = (serie: Series) => {
    const serieRef = seriesRef.current.find((s) => s.id === serie.id);

    if (serie.error) {
      deleteSeries(serie);
      return;
    }

    if (serieRef) {
      chart.current.removeSeries(serieRef.ref);
      seriesRef.current = seriesRef.current.filter((s) => s.id !== serie.id);

      deleteSeries(serie);

      if (seriesRef.current.length === 0 && tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = undefined;
      }
    }
  };

  useEffect(() => {
    if (chart.current) {
      const debouncedHandleTimeChange = debounce((newTimeRange) => {
        handleTimeChange(newTimeRange);
      }, 200);

      const handleVisibleTimeRangeChange = (newTimeRange: any) => {
        try {
          if (newTimeRange) {
            debouncedHandleTimeChange(newTimeRange);
          }
        } catch (error) {
          console.error(error);
        }
      };

      chart.current.timeScale().subscribeVisibleTimeRangeChange(handleVisibleTimeRangeChange);

      return () => {
        chart.current.timeScale().unsubscribeVisibleTimeRangeChange(handleVisibleTimeRangeChange);
        debouncedHandleTimeChange.cancel();
      };
    }
  }, [chart.current, handleTimeChange]);

  const hasSeriesData = series.length > 0 && series.some((s) => s.data.length > 0);

  const renderChipLabel = (chipLabel: string) => {
    if (['BTC_USDC', 'ETH_USDC'].includes(chipLabel)) {
      return chipLabel.split('_')[0] + ' PERPETUAL';
    }

    return `${chipLabel} PERPETUAL`;
  };

  const renderChips = () => {
    return (
      <div
        className="timeseries-chart-chip"
        style={chipStyle(isChartLoading)}
        ref={chipContainerRef}
      >
        {series.map((serie, i) => {
          const renderSymbol = (token: string) => {
            const cleanCurrency = (currency: string) => {
              const parts = currency.split('_');
              return parts[0];
            };

            const clearedTokenName = cleanCurrency(token);

            const src = Utils.getTokenIconPathBySign(clearedTokenName);
            const alt = Utils.getTokenNameBySign(clearedTokenName);

            const hasIcon = src && !src.includes('undefined');

            if (hasIcon) {
              return <img src={src} className="serie-icon" alt={alt} />;
            }

            return <div className="serie-icon" />;
          };

          const chipLabel =
            serie.type === RequestTypeEnum.PERPETUAL
              ? renderChipLabel(serie.currency)
              : serie.label;

          return (
            <VolSeriesChip
              removable
              key={i}
              series={serie}
              callBack={() => handleDeleteSerie(serie)}
              icon={renderSymbol(serie.currency)}
              label={chipLabel}
            ></VolSeriesChip>
          );
        })}
      </div>
    );
  };

  return (
    <div className="timeseries-chart" ref={timeSeriesWrapperRef}>
      {renderChips()}
      <div className="timeseries-chart-graph">
        {!hasSeriesData ? (
          <NoSeriesPlaceholder />
        ) : (
          <div
            className={`timeseries-chart-box-container ${!isChartLoading && 'chart-transition'}`}
            style={chipStyle(isChartLoading)}
          >
            <Box ref={chartContainerRef} height={chartHeight} className="chart-container">
              <div className="watermark" />
            </Box>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSeriesChart;
